import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export type UserType = 'internal' | 'external';

export type UserRow = {
  id: number;
  email: string;
  accountName: string;
  userType: UserType;
  merchantId: string | null;
  merchantRoleId: number | null;
  isSuperuser: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roleName: string | null;
  roleSlug: string | null;
  merchantName: string | null;
  /** MFA enrollment status. False until user enrolls via Security page. */
  mfaEnabled?: boolean;
  /** Suspend tracking (migration 0014). Null when the user is active. */
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  failedLoginAttempts?: number;
};

export type UsersPage = {
  rows: UserRow[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type CreateUserInput = {
  email: string;
  accountName: string;
  password: string;
  userType: UserType;
  merchantId?: string | null;
  merchantRoleId?: number | null;
};

export type PatchUserInput = {
  email?: string;
  accountName?: string;
  password?: string;
  merchantRoleId?: number | null;
};

export const usersApi = {
  list: (
    params: {
      scope?: UserType;
      merchantId?: string;
      search?: string;
      /** Role slugs to filter by (multi-select). Empty/omitted = all roles. */
      roleSlugs?: string[];
      cursor?: string;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.scope) q.set('scope', params.scope);
    if (params.merchantId) q.set('merchantId', params.merchantId);
    if (params.search) q.set('search', params.search);
    if (params.roleSlugs && params.roleSlugs.length > 0) {
      q.set('roleSlugs', params.roleSlugs.join(','));
    }
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return apiGet<UsersPage>(`/api/users${qs ? `?${qs}` : ''}`);
  },
  get: (id: number) => apiGet<UserRow>(`/api/users/${id}`),
  create: (input: CreateUserInput) => apiPost<UserRow>('/api/users', input),
  patch: (id: number, input: PatchUserInput) => apiPatch<{ ok: true }>(`/api/users/${id}`, input),
  remove: (id: number) => apiDelete<{ ok: true }>(`/api/users/${id}`),
  unsuspend: (id: number) =>
    apiPost<{ ok: true; userId: number; suspended: false; alreadyClear?: boolean }>(
      `/api/users/${id}/unsuspend`,
      {},
    ),
};
